import { Button, IconButton, styled, useTheme } from '@mui/material';
import { ReactComponent as ChevronRightIcon } from '../../../assets/icons/chevron-right.svg';
import { Typography } from '../../../components/Typography/Typography';
import { MonthlyTimesheet, UserRole } from '../../../types';
import { useAtomValue } from 'jotai';
import { useLocation, useNavigate, useParams } from 'react-router';
import { ROUTES } from '../../../constants/routes';
import { useMonthlyTimesheets } from '../../../queries/hooks/useMonthlyTimesheets';
import { isSmallScreenState, userState } from '../../../state/UIState';
import { getMonthLabel } from '../../../utils/formatters';
import { useMemo } from 'react';

type Props = {
  timesheet: MonthlyTimesheet;
};

export function TimesheetNavigation({ timesheet }: Props) {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const timesheetId = useParams().id;
  const isSmallScreen = useAtomValue(isSmallScreenState);
  const user = useAtomValue(userState);

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const yearQueryParam = params.get('year');
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const year = yearQueryParam || String(currentYear);

  const pathname = location.pathname;

  // This nav is shared by three detail pages (Accountant statuses, OP summary, Proxy summary).
  // Drive the dataset role AND the target route from the ROUTE being rendered — never from the
  // viewer's roles. A multi-role user (e.g. Proxy + Submitter, or Accountant + Submitter) must
  // paginate within the context of the page they opened, otherwise role precedence hijacks the
  // dataset (403 for a role they don't "lead with") and the arrows navigate to the wrong route.
  const { roleContext, navigateTo } = useMemo(() => {
    if (pathname.includes(ROUTES.ACCOUNTANT_TIMESHEETS_STATUSES)) {
      return {
        roleContext: UserRole.ACCOUNTANT,
        navigateTo: ROUTES.ACCOUNTANT_TIMESHEETS_STATUSES,
      };
    }
    if (pathname.includes(ROUTES.PROXY_TIMESHEETS_SUMMARY)) {
      return { roleContext: UserRole.PROXY, navigateTo: ROUTES.PROXY_TIMESHEETS_SUMMARY };
    }
    return { roleContext: UserRole.OPERATING_PARTNER, navigateTo: ROUTES.TIMESHEETS_SUMMARY };
  }, [pathname]);

  const currentTimesheetId = Number(timesheetId);
  const navigationLabel = `${getMonthLabel(timesheet.month)} ${timesheet.year}`;

  const { isLoading: allTimesheetsLoading, data: allTimesheets } = useMonthlyTimesheets({
    userId: user?.id as number,
    yearQueryParam: year,
    role: roleContext,
  });

  const { data: currentYearTimesheets } = useMonthlyTimesheets({
    userId: user?.id as number,
    yearQueryParam: String(currentYear),
    role: roleContext,
  });

  // Paginate within the timesheets of the SAME owner as the one currently open, scoped to the
  // selected year. For OP context the API already returns only the viewer's year; for Accountant /
  // Proxy it returns every owner's timesheets, so we narrow by owner + year here.
  const currentOwnerId = timesheet?.user?.id;

  const timesheetsIds = (allTimesheets || [])
    .filter((ts) => ts?.user?.id === currentOwnerId && ts.year === Number(year))
    .map((ts) => ts.id);

  const currentMonthTimesheetId = useMemo(
    () =>
      (currentYearTimesheets || []).find(
        (ts) =>
          ts?.user?.id === currentOwnerId && ts.month === currentMonth && ts.year === currentYear
      )?.id,
    [currentYearTimesheets, currentOwnerId, currentMonth, currentYear]
  );

  const havePrev = currentTimesheetId !== timesheetsIds[timesheetsIds.length - 1];
  const haveNext = currentTimesheetId !== timesheetsIds[0];

  return (
    <WeekPaginationWrap isSmallScreen={isSmallScreen}>
      <IconButton
        disabled={!havePrev || allTimesheetsLoading}
        onClick={() => {
          const currentIndex = timesheetsIds.findIndex((element) => element === currentTimesheetId);
          const id = timesheetsIds[currentIndex + 1];
          const queryParam = yearQueryParam ? `?year=${yearQueryParam}` : '';

          navigate(`/${navigateTo}/${id}${queryParam}`);
        }}
        sx={{
          svg: { path: { fill: colors.icon.accent, opacity: havePrev ? '1' : '0.3' } },
          backgroundColor: colors.surfaceBackground.highlighted,
          borderRadius: '4px',
          ':hover': {
            backgroundColor: colors.surfaceBackground.highlighted,
          },
        }}
      >
        <StyledPrevIcon />
      </IconButton>
      <IconButton
        sx={{
          svg: { path: { fill: colors.icon.accent, opacity: haveNext ? '1' : '0.3' } },
          backgroundColor: colors.surfaceBackground.highlighted,
          borderRadius: '4px',
          ':hover': {
            backgroundColor: colors.surfaceBackground.highlighted,
          },
        }}
        disabled={!haveNext || allTimesheetsLoading}
        onClick={() => {
          const currentIndex = timesheetsIds.findIndex((element) => element === currentTimesheetId);
          const id = timesheetsIds[currentIndex - 1];
          const queryParam = yearQueryParam ? `?year=${yearQueryParam}` : '';

          navigate(`/${navigateTo}/${id}${queryParam}`);
        }}
      >
        <StyledNextIcon />
      </IconButton>
      <Typography
        variant='body'
        color={colors.text.main}
        style={{
          lineHeight: 'inherit',
          fontSize: '1rem',
        }}
      >
        {navigationLabel}
      </Typography>
      <Button
        disabled={allTimesheetsLoading || currentMonth === timesheet.month}
        sx={{
          '&.Mui-disabled': {
            color: colors.textAccent.disabled,
          },
        }}
        onClick={() => {
          const queryParam = yearQueryParam ? `?year=${currentYear}` : '';
          navigate(`/${navigateTo}/${currentMonthTimesheetId}${queryParam}`);
        }}
      >
        {currentMonth !== timesheet.month ? 'Go to current month' : 'Current month'}
      </Button>
    </WeekPaginationWrap>
  );
}

const WeekPaginationWrap = styled('div')<{ isSmallScreen: boolean }>`
  display: flex;
  justify-content: ${({ isSmallScreen }) => (isSmallScreen ? 'flex-start' : 'center')};
  align-items: center;
  gap: ${({ isSmallScreen }) => (isSmallScreen ? '5px' : '10px')};
  width: ${({ isSmallScreen }) => (isSmallScreen ? '100%' : '30%')};
  padding: ${({ isSmallScreen }) => (isSmallScreen ? '8px 0' : '0px')};
`;

const StyledPrevIcon = styled(ChevronRightIcon)`
  transform: rotate(180deg);
`;

const StyledNextIcon = styled(ChevronRightIcon)``;
